import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { ImpactDashboardTrendPoint } from "@excalidraw-ms/shared";

interface ImpactTrendChartProps {
  points: ImpactDashboardTrendPoint[];
}

type TrendMetric = Exclude<keyof ImpactDashboardTrendPoint, "date">;

interface TrendMetricOption {
  key: TrendMetric;
  label: string;
  group: "Visitors" | "Active users" | "Cloud footprint" | "Activity";
  format?: "bytes";
}

const METRICS: TrendMetricOption[] = [
  { key: "dailyVisitors", label: "Daily visitors", group: "Visitors" },
  { key: "weeklyVisitors", label: "Weekly visitors", group: "Visitors" },
  { key: "monthlyVisitors", label: "Monthly visitors", group: "Visitors" },
  { key: "dau", label: "Daily active users", group: "Active users" },
  { key: "wau", label: "Weekly active users", group: "Active users" },
  { key: "mau", label: "Monthly active users", group: "Active users" },
  { key: "activeCloudDrawings", label: "Active cloud drawings", group: "Cloud footprint" },
  {
    key: "activeCloudStorageBytes",
    label: "Cloud storage used",
    group: "Cloud footprint",
    format: "bytes",
  },
  { key: "drawingsStarted", label: "Drawings started", group: "Activity" },
  { key: "cloudDrawingsCreated", label: "Cloud drawings created", group: "Activity" },
  { key: "collaborationSessions", label: "Collaboration sessions", group: "Activity" },
  { key: "aiAssistedSessions", label: "AI-assisted sessions", group: "Activity" },
  { key: "appUsageSessions", label: "App usage sessions", group: "Activity" },
];

const GROUPS: TrendMetricOption["group"][] = [
  "Visitors",
  "Active users",
  "Cloud footprint",
  "Activity",
];

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let result = value;
  let unit = -1;
  do {
    result /= 1024;
    unit += 1;
  } while (result >= 1024 && unit < units.length - 1);
  return `${result.toFixed(result >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatValue(value: number, option: TrendMetricOption): string {
  return option.format === "bytes"
    ? formatBytes(value)
    : new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
}

function niceCountMaximum(max: number): number {
  if (max <= 1) return 1;
  const roughStep = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const niceStep =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  return niceStep * magnitude * 4;
}

export default function ImpactTrendChart({ points }: ImpactTrendChartProps) {
  const [metric, setMetric] = useState<TrendMetric>("dau");
  const [activePoint, setActivePoint] = useState<number | null>(null);
  const [tabbablePoint, setTabbablePoint] = useState(0);
  const pointRefs = useRef<Array<SVGCircleElement | null>>([]);

  const width = 900;
  const height = 280;
  const left = 76;
  const right = 24;
  const top = 18;
  const bottom = 28;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const option = METRICS.find((candidate) => candidate.key === metric) ?? METRICS[0];
  const series = points.flatMap((point, sourceIndex) => {
    const value = point[metric];
    return value == null ? [] : [{ point, value, sourceIndex }];
  });
  const observedMax = Math.max(...series.map(({ value }) => value), 1);
  const max = option.format === "bytes" ? observedMax : niceCountMaximum(observedMax);
  const coordinates = series.map(({ value, sourceIndex }) => ({
    x: left + (sourceIndex / Math.max(points.length - 1, 1)) * plotWidth,
    y: top + (1 - value / max) * plotHeight,
  }));
  const line = series
    .map(({ sourceIndex, point }, index) => {
      const coordinate = coordinates[index];
      const previous = series[index - 1];
      const separated =
        !previous ||
        sourceIndex !== previous.sourceIndex + 1 ||
        Date.parse(point.date) - Date.parse(previous.point.date) > 86_400_000;
      return `${separated ? "M" : "L"}${coordinate.x},${coordinate.y}`;
    })
    .join(" ");
  const ticks = Array.from({ length: 5 }, (_, index) => (max * index) / 4);
  const latest = series.at(-1)?.value;
  const selectMetric = (value: string) => {
    const selected = METRICS.find((candidate) => candidate.key === value);
    if (selected) {
      setMetric(selected.key);
      setActivePoint(null);
      setTabbablePoint(0);
    }
  };
  const focusPoint = (index: number) => {
    setTabbablePoint(index);
    setActivePoint(index);
    pointRefs.current[index]?.focus();
  };
  const handlePointKeyDown = (event: KeyboardEvent<SVGCircleElement>, index: number) => {
    let nextIndex: number | null = null;

    if (event.key === "ArrowLeft") {
      nextIndex = Math.max(index - 1, 0);
    } else if (event.key === "ArrowRight") {
      nextIndex = Math.min(index + 1, series.length - 1);
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = series.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      focusPoint(nextIndex);
    }
  };
  const currentTabbablePoint = Math.min(tabbablePoint, series.length - 1);
  const active = activePoint === null ? null : series[activePoint];
  const activeCoordinate = activePoint === null ? null : coordinates[activePoint];
  const tooltipX = activeCoordinate
    ? Math.min(Math.max(activeCoordinate.x, left + 80), width - right - 80)
    : 0;
  const tooltipY = activeCoordinate
    ? activeCoordinate.y < top + 60
      ? activeCoordinate.y + 12
      : activeCoordinate.y - 56
    : 0;

  return (
    <div className="impact-chart">
      <div className="impact-chart__controls">
        <label>
          Metric
          <select value={metric} onChange={(event) => selectMetric(event.target.value)}>
            {GROUPS.map((group) => (
              <optgroup key={group} label={group}>
                {METRICS.filter((candidate) => candidate.group === group).map((candidate) => (
                  <option key={candidate.key} value={candidate.key}>
                    {candidate.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <span className="impact-chart__latest">
          Latest{" "}
          <strong>{latest === undefined ? "Not available" : formatValue(latest, option)}</strong>
        </span>
      </div>
      {series.length < 2 ? (
        <div className="impact-chart__empty">
          Trend data will appear after two daily snapshots for this metric.
        </div>
      ) : (
        <svg viewBox={`0 0 ${width} ${height}`} role="group" aria-label={`${option.label} trend`}>
          {ticks.map((tick) => {
            const y = top + (1 - tick / max) * plotHeight;
            return (
              <g key={tick} className="impact-chart__axis">
                <line x1={left} y1={y} x2={width - right} y2={y} />
                <text x={left - 10} y={y} textAnchor="end" dominantBaseline="middle">
                  {formatValue(tick, option)}
                </text>
              </g>
            );
          })}
          <path className="impact-chart__line" d={line} />
          {series.map(({ point, value }, index) => {
            const coordinate = coordinates[index];
            const label = `${point.date}: ${formatValue(value, option)}`;
            return (
              <g key={point.date}>
                <circle
                  className="impact-chart__point"
                  cx={coordinate.x}
                  cy={coordinate.y}
                  r="3"
                  opacity={series.length <= 90 || activePoint === index ? 1 : 0}
                />
                <circle
                  ref={(element) => {
                    pointRefs.current[index] = element;
                  }}
                  className="impact-chart__point-hit"
                  cx={coordinate.x}
                  cy={coordinate.y}
                  r="9"
                  role="img"
                  tabIndex={index === currentTabbablePoint ? 0 : -1}
                  aria-label={label}
                  onMouseEnter={() => setActivePoint(index)}
                  onMouseLeave={() => setActivePoint(null)}
                  onFocus={() => {
                    setTabbablePoint(index);
                    setActivePoint(index);
                  }}
                  onBlur={() => setActivePoint(null)}
                  onKeyDown={(event) => handlePointKeyDown(event, index)}
                />
              </g>
            );
          })}
          {active && activeCoordinate && (
            <g className="impact-chart__tooltip" role="tooltip">
              <rect x={tooltipX - 75} y={tooltipY} width="150" height="44" rx="7" />
              <text x={tooltipX} y={tooltipY + 16} textAnchor="middle">
                {active.point.date}
              </text>
              <text x={tooltipX} y={tooltipY + 34} textAnchor="middle">
                {formatValue(active.value, option)}
              </text>
            </g>
          )}
        </svg>
      )}
      <div className="impact-chart__dates">
        <span>{points[0]?.date}</span>
        <span>{points.at(-1)?.date}</span>
      </div>
    </div>
  );
}
